/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
/**
 * Built-in transition helper functions for type-based animations.
 *
 * These helpers can be used directly or as templates for custom transitions.
 * Each helper receives a TransitionContext and returns a TransitionResult.
 */

import { Box3, Euler, Matrix4, Mesh, Object3D, Vector3 } from 'three';

import type { TransitionContext, TransitionHelper, TransitionResult } from '../../engine/types/engine.types';
import { easeInOutCubic, easeOutCubic } from './easing';
import { generateQuadraticBezierPoints } from './path-interpolation';

// Import and re-export material state management from extracted module
import {
	prepareMaterialsForFade,
	restoreMaterials,
	setObjectOpacity
} from './transition-helpers/material-state';

export { prepareMaterialsForFade, restoreMaterials, setObjectOpacity };

/**
 * Helper to convert world position to local position for an object with a parent
 */
const worldToLocal = (worldPos: Vector3, parent: Object3D): Vector3 => {
	const inverseMatrix = new Matrix4().copy(parent.matrixWorld)
		.invert();
	return worldPos.clone().applyMatrix4(inverseMatrix);
};

/**
 * Helper to convert local position to world position for an object with a parent
 */
const localToWorld = (localPos: Vector3, parent: Object3D): Vector3 => {
	return localPos.clone().applyMatrix4(parent.matrixWorld);
};

/**
 * Normalize angle to -PI to PI range
 */
const normalizeAngle = (angle: number): number => {
	while (angle > Math.PI) angle -= 2 * Math.PI;
	while (angle < -Math.PI) angle += 2 * Math.PI;
	return angle;
};

/**
 * Find shortest rotation path between two angles
 */
const shortestRotationPath = (from: number, to: number): number => {
	const diff = normalizeAngle(to - from);
	return from + diff;
};

// ========== Built-in Transition Helpers ==========

/**
 * Fade in: opacity 0 → 1
 * Simple fade animation for panels, countertops, etc.
 */
export const fadeIn: TransitionHelper = (ctx: TransitionContext): TransitionResult => {
	const { object, metadata, animationManager, duration: configDuration } = ctx;
	const duration = configDuration ?? 400;

	// Prepare materials
	prepareMaterialsForFade(object);
	setObjectOpacity(object, 0);

	const promise = animationManager.animateOpacity(
		`fade-in-${metadata.id}`,
		0,
		1,
		duration,
		(opacity) => setObjectOpacity(object, opacity),
		{ easing: easeOutCubic }
	).then(() => {
		// Restore only transparent flag, but keep opacity at 1 (fully visible)
		// This fixes a bug where restoreMaterials would restore opacity to 0
		// if the object was already invisible when prepareMaterialsForFade was called
		restoreMaterials(object);
		setObjectOpacity(object, 1);
	});

	return {
		promise,
		cancel: () => animationManager.stop(`fade-in-${metadata.id}`)
	};
};

/**
 * Fade out with scale (dissolve effect)
 * Used for removal animations
 */
export const dissolve: TransitionHelper = (ctx: TransitionContext): TransitionResult => {
	const { object, metadata, animationManager, duration: configDuration } = ctx;
	const duration = configDuration ?? 400;
	const scaleTarget = 0.8;

	prepareMaterialsForFade(object);

	const fadePromise = animationManager.animateOpacity(
		`dissolve-fade-${metadata.id}`,
		1,
		0,
		duration,
		(opacity) => setObjectOpacity(object, opacity),
		{ easing: easeInOutCubic }
	);

	const scalePromise = animationManager.animateScale(
		`dissolve-scale-${metadata.id}`,
		1,
		scaleTarget,
		duration,
		(scale) => object.scale.setScalar(scale),
		{ easing: easeInOutCubic }
	);

	const promise = Promise.all([fadePromise, scalePromise]).then(() => {
		restoreMaterials(object);
	});

	return {
		promise,
		cancel: () => {
			animationManager.stop(`dissolve-fade-${metadata.id}`);
			animationManager.stop(`dissolve-scale-${metadata.id}`);
		}
	};
};

/**
 * Scale in: scale 0 → 1 with fade
 * Pop-in effect for quick additions
 */
export const scaleIn: TransitionHelper = (ctx: TransitionContext): TransitionResult => {
	const { object, metadata, animationManager, duration: configDuration } = ctx;
	const duration = configDuration ?? 300;

	prepareMaterialsForFade(object);
	setObjectOpacity(object, 0);
	object.scale.setScalar(0.5);

	const fadePromise = animationManager.animateOpacity(
		`scale-in-fade-${metadata.id}`,
		0,
		1,
		duration,
		(opacity) => setObjectOpacity(object, opacity),
		{ easing: easeOutCubic }
	);

	const scalePromise = animationManager.animateScale(
		`scale-in-scale-${metadata.id}`,
		0.5,
		1,
		duration,
		(scale) => object.scale.setScalar(scale),
		{ easing: easeOutCubic }
	);

	const promise = Promise.all([fadePromise, scalePromise]).then(() => {
		restoreMaterials(object);
		setObjectOpacity(object, 1); // Ensure fully visible after fade-in
	});

	return {
		promise,
		cancel: () => {
			animationManager.stop(`scale-in-fade-${metadata.id}`);
			animationManager.stop(`scale-in-scale-${metadata.id}`);
		}
	};
};

/**
 * Fly from presentation point to final position with arc trajectory.
 * Animation phases:
 * 1. FADE IN: Object appears at start position, Y rotation at 0
 * 2. PAUSE: Brief pause for viewer to register the appliance
 * 3. FLIGHT: Object flies along arc to target, scaling up and rotating to final orientation
 *
 * When worldCenter is provided, animation uses world coordinates for the path,
 * converting to local coordinates when setting object.position.
 */
export const flyFromPresentationPoint: TransitionHelper = (ctx: TransitionContext): TransitionResult => {
	const { object, metadata, animationManager, islandCenter, worldCenter, targetPosition, originalScale, phaseTimings } = ctx;

	// Phase timings with defaults
	const fadeInDuration = phaseTimings?.fadeIn ?? 300;
	const pauseDuration = phaseTimings?.pause ?? 200;
	const flightDuration = phaseTimings?.flight ?? 600;
	const totalDuration = fadeInDuration + pauseDuration + flightDuration;

	// Use original scale (Collada models default to 10) or fallback to current scale
	const targetScale = originalScale ?? object.scale.x;

	const { parent } = object;

	// Fallback to fadeIn if no position available
	if (!targetPosition) {
		return fadeIn(ctx);
	}

	// Fallback to fadeIn if no center available
	if (!worldCenter && !islandCenter) {
		return fadeIn(ctx);
	}

	// Ensure parent's world matrix is up-to-date for coordinate conversions
	if (parent) {
		parent.updateMatrixWorld(true);
	}

	// Calculate start and end positions in WORLD space
	// worldCenter is always in world coordinates (scene center above kitchen)
	// targetPosition is in LOCAL coordinates (relative to parent)
	let presentationPointWorld: Vector3;
	let endPosWorld: Vector3;
	const endPosLocal = targetPosition.clone();

	if (worldCenter) {
		// Use world center directly - this is the scene center above the kitchen
		presentationPointWorld = new Vector3(worldCenter.x, worldCenter.y, worldCenter.z);
		// Convert local target to world for path calculation
		endPosWorld = parent ? localToWorld(targetPosition, parent) : targetPosition.clone();
	} else if (islandCenter) {
		// islandCenter is in local coordinates, convert to world
		const localStart = new Vector3(islandCenter.x, islandCenter.y, islandCenter.z);
		presentationPointWorld = parent ? localToWorld(localStart, parent) : localStart;
		endPosWorld = parent ? localToWorld(targetPosition, parent) : targetPosition.clone();
	} else {
		// Should never reach here due to earlier check, but TypeScript needs this
		return fadeIn(ctx);
	}

	// Calculate bounding box center offset to center the object's visual middle at the presentation point
	// The object's origin (position) may not be at its visual center
	// IMPORTANT: Compute bounding box in pure LOCAL space (without ANY rotation)
	// This gives us the true geometry center that we can then properly transform
	const savedRotation = object.rotation.clone();
	const savedParent = object.parent;
	const savedPosition = object.position.clone();

	// Temporarily detach from parent and reset ALL rotations to compute pure LOCAL bounding box
	if (savedParent) {
		savedParent.remove(object);
	}
	object.position.set(0, 0, 0);
	object.rotation.set(0, 0, 0);  // Reset ALL rotations, not just Y
	object.updateMatrixWorld(true);

	const localBoundingBox = new Box3().setFromObject(object);
	const localBoundingBoxCenter = new Vector3();
	localBoundingBox.getCenter(localBoundingBoxCenter);

	// Restore hierarchy and original rotation
	object.position.copy(savedPosition);
	object.rotation.copy(savedRotation);
	if (savedParent) {
		savedParent.add(object);
	}
	object.updateMatrixWorld(true);

	// Get parent's LOCAL Y rotation to compute what Y rotation makes object face camera
	// We only counter-rotate Y axis (to turn and face camera), preserving the object's X/Z rotations
	// which are part of how the model is oriented (e.g., a sink rotated -90° X to lie flat)
	let parentLocalRotationY = 0;

	if (parent) {
		// Use only the parent's LOCAL Y rotation (container's horizontal orientation)
		parentLocalRotationY = parent.rotation.y;
	}

	// Start rotation: preserve object's X and Z rotations, only counter-rotate Y
	// This keeps appliances oriented correctly (e.g., sinks lying flat) while facing the camera
	const startRotationY = -parentLocalRotationY;
	const startRotationEuler = new Euler(
		object.rotation.x,                    // Preserve X (tilt)
		startRotationY,                       // Counter-rotate Y to face camera
		object.rotation.z,                    // Preserve Z (roll)
		object.rotation.order
	);

	// Calculate the visual center offset accounting for the object's FULL local rotation
	// localBoundingBoxCenter was computed with NO rotation, so we need to apply the full startRotationEuler
	// Create a rotation matrix from startRotationEuler and apply it to the center
	const rotationMatrix = new Matrix4().makeRotationFromEuler(startRotationEuler);
	const centerInParentSpace = localBoundingBoxCenter.clone().applyMatrix4(rotationMatrix);

	// Scale by 0.5 for initial 50% scale
	const scaledCenterInParentSpace = centerInParentSpace.clone().multiplyScalar(0.5);

	// Convert presentation point to parent's local space
	const presentationPointLocal = parent ? worldToLocal(presentationPointWorld, parent) : presentationPointWorld.clone();

	// Object's local position = presentation point (in parent space) - center offset (in parent space)
	const startPosLocal = presentationPointLocal.clone().sub(scaledCenterInParentSpace);

	// Also compute world position for debug and path calculation
	const startPosWorld = parent ? localToWorld(startPosLocal, parent) : startPosLocal.clone();

	// IMPORTANT: Determine target rotation FIRST, before computing end position
	// This handles duplicate calls where object.rotation has already been modified
	const originalTargetKey = '__flyTransitionOriginalRotation';
	let targetRotation: Euler;

	if (object.userData[originalTargetKey]) {
		// Duplicate call - use the previously saved original target
		const saved = object.userData[originalTargetKey] as { x: number; y: number; z: number; order: string };
		targetRotation = new Euler(saved.x, saved.y, saved.z, saved.order as Euler['order']);
	} else {
		// First call - capture and save the original rotation
		targetRotation = new Euler(object.rotation.x, object.rotation.y, object.rotation.z, object.rotation.order);
		object.userData[originalTargetKey] = {
			x: targetRotation.x,
			y: targetRotation.y,
			z: targetRotation.z,
			order: targetRotation.order
		};
	}

	// Compute the visual center position at the END of the animation
	// endPosWorld is where the object's origin will be, we need to add the center offset
	// endCenterOffset is in parent-local space (object rotation applied), so we need to
	// transform it to world space by applying the parent's world rotation
	// IMPORTANT: Use targetRotation (not object.rotation) to handle duplicate calls correctly
	const targetRotMatrix = new Matrix4().makeRotationFromEuler(targetRotation);
	const endCenterOffsetLocal = localBoundingBoxCenter.clone().applyMatrix4(targetRotMatrix);
	// Transform from parent-local to world space by applying parent's Y rotation
	const parentWorldRotationY = parentLocalRotationY; // For now, assuming parent's world Y rotation = local Y rotation
	const cosParent = Math.cos(parentWorldRotationY);
	const sinParent = Math.sin(parentWorldRotationY);
	const endCenterOffsetWorld = new Vector3(
		endCenterOffsetLocal.x * cosParent - endCenterOffsetLocal.z * sinParent,
		endCenterOffsetLocal.y,
		endCenterOffsetLocal.x * sinParent + endCenterOffsetLocal.z * cosParent
	);
	// Note: no scale multiplier here because at the end the object is at full scale (targetScale)
	const endCenterWorld = endPosWorld.clone().add(endCenterOffsetWorld);

	// The path goes from visual center (presentationPointWorld) to visual center (endCenterWorld)
	// startPosWorld is the object origin, but for the path we use the center positions
	const startCenterWorld = presentationPointWorld.clone();

	// Calculate improved control point for arc in world space
	// Arc height is proportional to horizontal distance for a natural-looking trajectory
	const horizontalDist = Math.sqrt((endCenterWorld.x - startCenterWorld.x) ** 2 +
		(endCenterWorld.z - startCenterWorld.z) ** 2);
	const arcHeight = Math.max(10, horizontalDist * 0.15);

	// Control point at 40% along path (biased toward start for acceleration feel)
	const controlPointWorld = startCenterWorld.clone().lerp(endCenterWorld, 0.4);
	controlPointWorld.y = Math.max(startCenterWorld.y, endCenterWorld.y) + arcHeight;

	// Generate bezier path in world space for smooth arc - this is the path of the VISUAL CENTER
	const pathWorld = generateQuadraticBezierPoints(startCenterWorld, controlPointWorld, endCenterWorld, 32);

	// Debug visualization: show start/end vectors when debug manager is available and visible
	const { debugManager } = ctx;
	const debugId = `fly-${metadata.id}`;

	// Face vectors - calculate offsets from object LOCAL origin to face centers
	// These are in LOCAL space and will be transformed to world during animation
	const isBoundingBoxValid = Number.isFinite(localBoundingBox.max.x) &&
		Number.isFinite(localBoundingBox.max.y) &&
		Number.isFinite(localBoundingBox.max.z);

	// Store face offset data for animation updates (outside if block so updateFaceVectors can access it)
	// These offsets are in LOCAL space (object's coordinate system with no rotation)
	let faceVectorOffsets: {
		frontOffset: Vector3;  // Local offset from object origin to front face center
		topOffset: Vector3;    // Local offset from object origin to top face center
	} | null = null;

	if (isBoundingBoxValid) {
		// Calculate offsets in LOCAL space (from object's local origin to face centers)
		// Front face is at local +Z (max.z), top face is at local +Y (max.y)
		const frontCenterLocal = new Vector3(localBoundingBoxCenter.x, localBoundingBoxCenter.y, localBoundingBox.max.z);
		const topCenterLocal = new Vector3(localBoundingBoxCenter.x, localBoundingBox.max.y, localBoundingBoxCenter.z);

		// Store LOCAL offsets (these will be transformed by world rotation during animation)
		faceVectorOffsets = {
			frontOffset: frontCenterLocal,
			topOffset: topCenterLocal
		};
	}

	// Helper to update face vectors during animation (defined outside if block so it's accessible from animation callbacks)
	const updateFaceVectors = (currentWorldPos: Vector3, currentRotationY: number, currentScale: number): void => {
		if (!faceVectorOffsets || !debugManager?.visible) return;

		// Full world rotation = parent's world rotation + object's local rotation
		const worldRotationY = parentLocalRotationY + currentRotationY;

		// Scale the LOCAL offsets
		const scaledFrontOffset = faceVectorOffsets.frontOffset.clone().multiplyScalar(currentScale / targetScale);
		const scaledTopOffset = faceVectorOffsets.topOffset.clone().multiplyScalar(currentScale / targetScale);

		// Apply WORLD rotation to local offsets (rotate around Y axis by full world rotation)
		const cosR = Math.cos(worldRotationY);
		const sinR = Math.sin(worldRotationY);
		const rotatedFrontOffset = new Vector3(
			scaledFrontOffset.x * cosR - scaledFrontOffset.z * sinR,
			scaledFrontOffset.y,
			scaledFrontOffset.x * sinR + scaledFrontOffset.z * cosR
		);
		const rotatedTopOffset = new Vector3(
			scaledTopOffset.x * cosR - scaledTopOffset.z * sinR,
			scaledTopOffset.y,
			scaledTopOffset.x * sinR + scaledTopOffset.z * cosR
		);

		const currentFrontCenter = currentWorldPos.clone().add(rotatedFrontOffset);
		const currentTopCenter = currentWorldPos.clone().add(rotatedTopOffset);

		// Front normal rotated by WORLD rotation
		// Front face is at local +Z, so base normal is (0, 0, 1)
		// Rotating (0, 0, 1) around Y by angle θ gives (sin(θ), 0, cos(θ))
		const frontNormal = new Vector3(sinR, 0, cosR);
		const frontEnd = currentFrontCenter.clone().add(frontNormal.clone().multiplyScalar(18));

		// Top normal (always up)
		const topNormal = new Vector3(0, 1, 0);
		const topEnd = currentTopCenter.clone().add(topNormal.clone().multiplyScalar(18));

		// Update positions
		debugManager.updateSphere(`${debugId}-front-face`, currentFrontCenter);
		debugManager.updateLine(`${debugId}-front-vector`, currentFrontCenter, frontEnd);
		debugManager.updateSphere(`${debugId}-top-face`, currentTopCenter);
		debugManager.updateLine(`${debugId}-top-vector`, currentTopCenter, topEnd);
	};

	if (debugManager?.visible) {
		// Start point sphere (green) - the visual center at presentation point
		debugManager.addSphere({
			id: `${debugId}-start`,
			position: startCenterWorld.clone(),
			radius: 0.25,
			color: 0x00ff00,
			label: 'Start Center'
		});
		// End point sphere (red) - the visual center at target position
		debugManager.addSphere({
			id: `${debugId}-end`,
			position: endCenterWorld.clone(),
			radius: 0.25,
			color: 0xff0000,
			label: 'End Center'
		});
		// Control point sphere (yellow) - the arc peak
		debugManager.addSphere({
			id: `${debugId}-control`,
			position: controlPointWorld.clone(),
			radius: 0.25,
			color: 0xffff00,
			label: 'Arc Control'
		});
		// Line from start to end (cyan)
		debugManager.addLine({
			id: `${debugId}-path`,
			start: startCenterWorld.clone(),
			end: endCenterWorld.clone(),
			color: 0x00ffff
		});
		// Line from start to control (yellow)
		debugManager.addLine({
			id: `${debugId}-arc1`,
			start: startCenterWorld.clone(),
			end: controlPointWorld.clone(),
			color: 0xffff00
		});
		// Line from control to end (yellow)
		debugManager.addLine({
			id: `${debugId}-arc2`,
			start: controlPointWorld.clone(),
			end: endCenterWorld.clone(),
			color: 0xffff00
		});

		// Create face vectors at START position initially (they'll be updated during animation)
		if (faceVectorOffsets) {
			// At start: local rotation = -parentLocalRotationY, so world rotation = 0 (facing camera)
			const initialWorldRotationY = 0;
			const cosR = Math.cos(initialWorldRotationY);  // cos(0) = 1
			const sinR = Math.sin(initialWorldRotationY);  // sin(0) = 0

			// Scale and rotate local offsets by world rotation
			const scaledFrontOffset = faceVectorOffsets.frontOffset.clone().multiplyScalar(0.5); // 50% initial scale
			const scaledTopOffset = faceVectorOffsets.topOffset.clone().multiplyScalar(0.5);

			const rotatedFrontOffset = new Vector3(
				scaledFrontOffset.x * cosR - scaledFrontOffset.z * sinR,
				scaledFrontOffset.y,
				scaledFrontOffset.x * sinR + scaledFrontOffset.z * cosR
			);
			const rotatedTopOffset = new Vector3(
				scaledTopOffset.x * cosR - scaledTopOffset.z * sinR,
				scaledTopOffset.y,
				scaledTopOffset.x * sinR + scaledTopOffset.z * cosR
			);

			const initialFrontCenter = startPosWorld.clone().add(rotatedFrontOffset);
			const initialTopCenter = startPosWorld.clone().add(rotatedTopOffset);

			// Front normal rotated by initial world rotation
			const initialFrontNormal = new Vector3(sinR, 0, cosR);
			const initialFrontEnd = initialFrontCenter.clone().add(initialFrontNormal.clone().multiplyScalar(18));

			// Top face (calculate early for logging)
			const initialTopNormal = new Vector3(0, 1, 0);
			const initialTopEnd = initialTopCenter.clone().add(initialTopNormal.clone().multiplyScalar(18));

			debugManager.addSphere({
				id: `${debugId}-front-face`,
				position: initialFrontCenter.clone(),
				radius: 1,
				color: 0xffffcc,
				label: 'Front'
			});
			debugManager.addLine({
				id: `${debugId}-front-vector`,
				start: initialFrontCenter.clone(),
				end: initialFrontEnd,
				color: 0xff0000  // Bright red for visibility
			});

			// Top face sphere and line
			debugManager.addSphere({
				id: `${debugId}-top-face`,
				position: initialTopCenter.clone(),
				radius: 1,
				color: 0xffffcc,
				label: 'Top'
			});
			debugManager.addLine({
				id: `${debugId}-top-vector`,
				start: initialTopCenter.clone(),
				end: initialTopEnd,
				color: 0x00ff00  // Bright green for visibility
			});
		}
	}

	// Initial state: at center, invisible, 50% scale, facing camera (using full rotation)
	object.position.copy(startPosLocal);
	object.rotation.set(startRotationEuler.x, startRotationEuler.y, startRotationEuler.z, startRotationEuler.order);
	prepareMaterialsForFade(object);
	setObjectOpacity(object, 0);
	object.scale.setScalar(targetScale * 0.5);

	// Force world matrix update so the rotation takes effect immediately
	object.updateMatrixWorld(true);

	// Track animation state for cancellation
	let cancelled = false;
	let pauseTimeoutId: ReturnType<typeof setTimeout> | null = null;

	// Calculate final rotations using shortest path for each axis
	const finalRotationX = shortestRotationPath(startRotationEuler.x, targetRotation.x);
	const finalRotationY = shortestRotationPath(startRotationEuler.y, targetRotation.y);
	const finalRotationZ = shortestRotationPath(startRotationEuler.z, targetRotation.z);

	// Execute phased animation sequence
	const executeAnimation = async (): Promise<void> => {
		// PHASE 1: Fade In
		// Object becomes visible while stationary at start position, facing camera
		await animationManager.animateOpacity(
			`fly-fade-${metadata.id}`,
			0,
			1,
			fadeInDuration,
			(opacity) => setObjectOpacity(object, opacity),
			{ easing: easeOutCubic }
		);

		if (cancelled) return;

		// PHASE 2: Pause
		// Brief pause for viewer to register the appliance
		if (pauseDuration > 0) {
			await new Promise<void>((resolve) => {
				pauseTimeoutId = setTimeout(() => {
					pauseTimeoutId = null;
					resolve();
				}, pauseDuration);
			});
		}

		if (cancelled) return;

		// PHASE 3: Flight
		// Concurrent position, scale, and rotation animations
		// Uses easeInOutCubic for smooth acceleration/deceleration
		// Track current values for face vector updates
		let currentScale = targetScale * 0.5;
		let currentRotationY = startRotationEuler.y;

		await Promise.all([
			// Position along arc path
			// The path represents where the visual CENTER should be
			// We compute the object origin position by subtracting the rotated center offset
			new Promise<void>((resolve) => {
				animationManager.animateAlongPath(
					`fly-pos-${metadata.id}`,
					pathWorld,
					flightDuration,
					(centerWorldPos) => {
						// Get current rotation from the object (being animated by other callbacks)
						const currentRotationEuler = new Euler(object.rotation.x, object.rotation.y, object.rotation.z, object.rotation.order);

						// Compute center offset based on current rotation and scale
						const currentRotMatrix = new Matrix4().makeRotationFromEuler(currentRotationEuler);
						const currentCenterOffset = localBoundingBoxCenter.clone().applyMatrix4(currentRotMatrix);
						currentCenterOffset.multiplyScalar(currentScale / targetScale);

						// Convert center world position to local, then subtract offset to get origin
						if (parent) {
							const centerLocalPos = worldToLocal(centerWorldPos, parent);
							const originLocalPos = centerLocalPos.clone().sub(currentCenterOffset);
							object.position.copy(originLocalPos);
						} else {
							const originWorldPos = centerWorldPos.clone().sub(currentCenterOffset);
							object.position.copy(originWorldPos);
						}
						// Update face vectors with current position, rotation, and scale
						updateFaceVectors(centerWorldPos, currentRotationY, currentScale);
					},
					{ easing: easeInOutCubic, onComplete: resolve }
				);
			}),

			// Scale up from 50% to 100%
			animationManager.animateScale(
				`fly-scale-${metadata.id}`,
				targetScale * 0.5,
				targetScale,
				flightDuration,
				(scale) => {
					currentScale = scale;
					object.scale.setScalar(scale);
				},
				{ easing: easeOutCubic }
			),

			// Rotation X from camera-facing to target orientation
			new Promise<void>((resolve) => {
				animationManager.animateValue(
					`fly-rot-x-${metadata.id}`,
					startRotationEuler.x,
					finalRotationX,
					flightDuration,
					(rot) => {
						object.rotation.x = rot;
					},
					{ easing: easeInOutCubic, onComplete: resolve }
				);
			}),

			// Rotation Y from camera-facing to target orientation
			new Promise<void>((resolve) => {
				animationManager.animateValue(
					`fly-rot-y-${metadata.id}`,
					startRotationEuler.y,
					finalRotationY,
					flightDuration,
					(rot) => {
						currentRotationY = rot;
						object.rotation.y = rot;
					},
					{ easing: easeInOutCubic, onComplete: resolve }
				);
			}),

			// Rotation Z from camera-facing to target orientation
			new Promise<void>((resolve) => {
				animationManager.animateValue(
					`fly-rot-z-${metadata.id}`,
					startRotationEuler.z,
					finalRotationZ,
					flightDuration,
					(rot) => {
						object.rotation.z = rot;
					},
					{ easing: easeInOutCubic, onComplete: resolve }
				);
			})
		]);

		// Cleanup and ensure exact final state
		restoreMaterials(object);
		setObjectOpacity(object, 1); // Ensure fully visible after fade-in

		object.position.copy(endPosLocal);
		object.scale.setScalar(targetScale);
		object.rotation.set(targetRotation.x, targetRotation.y, targetRotation.z, targetRotation.order);
		// Clean up saved rotation and debug visualization
		delete object.userData[originalTargetKey];
		cleanupDebug();
	};

	// Helper to clean up debug visualization
	const cleanupDebug = (): void => {
		if (debugManager?.visible) {
			debugManager.removeSphere(`${debugId}-start`);
			debugManager.removeSphere(`${debugId}-end`);
			debugManager.removeSphere(`${debugId}-control`);
			debugManager.removeLine(`${debugId}-path`);
			debugManager.removeLine(`${debugId}-arc1`);
			debugManager.removeLine(`${debugId}-arc2`);
			// Face vectors
			debugManager.removeSphere(`${debugId}-front-face`);
			debugManager.removeLine(`${debugId}-front-vector`);
			debugManager.removeSphere(`${debugId}-top-face`);
			debugManager.removeLine(`${debugId}-top-vector`);
		}
		// Clean up saved original rotation
		delete object.userData[originalTargetKey];
	};

	// Failsafe: ensure final state after total duration + buffer
	const failsafeTimeout = setTimeout(() => {
		if (!cancelled) {
			restoreMaterials(object);
			object.position.copy(endPosLocal);
			object.scale.setScalar(targetScale);
			object.rotation.set(targetRotation.x, targetRotation.y, targetRotation.z, targetRotation.order);
			setObjectOpacity(object, 1);
			delete object.userData[originalTargetKey];
			cleanupDebug();
		}
	}, totalDuration + 500);

	const promise = executeAnimation()
		.then(() => {
			clearTimeout(failsafeTimeout);
		})
		.catch(() => {
			clearTimeout(failsafeTimeout);
			// Ensure final state even on error
			restoreMaterials(object);
			setObjectOpacity(object, 1); // Ensure fully visible
			object.position.copy(endPosLocal);
			object.scale.setScalar(targetScale);
			object.rotation.set(targetRotation.x, targetRotation.y, targetRotation.z, targetRotation.order);
			delete object.userData[originalTargetKey];
			cleanupDebug();
		});

	return {
		promise,
		cancel: () => {
			cancelled = true;
			if (pauseTimeoutId) {
				clearTimeout(pauseTimeoutId);
			}
			animationManager.stop(`fly-fade-${metadata.id}`);
			animationManager.stop(`fly-scale-${metadata.id}`);
			animationManager.stop(`fly-pos-${metadata.id}`);
			animationManager.stop(`fly-rot-x-${metadata.id}`);
			animationManager.stop(`fly-rot-y-${metadata.id}`);
			animationManager.stop(`fly-rot-z-${metadata.id}`);
			cleanupDebug();
		}
	};
};

/**
 * Smooth position tween between two points
 * Used for move animations
 */
export const smoothMove: TransitionHelper = (ctx: TransitionContext): TransitionResult => {
	const { object, metadata, animationManager, startPosition, targetPosition, duration: configDuration } = ctx;
	const duration = configDuration ?? 600;

	// No-op if positions not provided
	if (!startPosition || !targetPosition) {
		return { promise: Promise.resolve() };
	}

	const promise = new Promise<void>((resolve) => {
		animationManager.animatePosition(
			`move-${metadata.id}`,
			startPosition,
			targetPosition,
			duration,
			(pos) => object.position.copy(pos),
			{ easing: easeInOutCubic, onComplete: resolve }
		);
	});

	return {
		promise,
		cancel: () => animationManager.stop(`move-${metadata.id}`)
	};
};

/**
 * Smooth rotation tween
 * Used for rotate animations
 */
export const smoothRotate: TransitionHelper = (ctx: TransitionContext): TransitionResult => {
	const { object, metadata, animationManager, startRotation, targetRotation, duration: configDuration } = ctx;
	const duration = configDuration ?? 400;

	// No-op if rotations not provided
	if (!startRotation || !targetRotation) {
		return { promise: Promise.resolve() };
	}

	const promise = new Promise<void>((resolve) => {
		const startY = startRotation.y;
		const endY = targetRotation.y;

		animationManager.animateValue(
			`rotate-${metadata.id}`,
			startY,
			endY,
			duration,
			(y) => {
				object.rotation.y = y;
			},
			{ easing: easeInOutCubic, onComplete: resolve }
		);
	});

	return {
		promise,
		cancel: () => animationManager.stop(`rotate-${metadata.id}`)
	};
};

/**
 * Instant transition (no animation)
 * Used for cutouts and debug objects
 */
export const instant: TransitionHelper = (): TransitionResult => {
	return { promise: Promise.resolve() };
};

/**
 * Slide in from offset position based on visible faces.
 *
 * The offset direction depends on the appliance's visible faces:
 * - Front only: 18" from the front plane (toward camera, +Z in local space)
 * - Front and top: 18" at 45 degrees between front and top planes
 * - Top only: 18" from top plane (upward, +Y in local space)
 *
 * Animation phases:
 * 1. FADE IN: Object appears at offset position
 * 2. SLIDE: Object slides to target position with ease-in-out timing
 *
 * Visible faces are determined from metadata.visibleFaces or object.userData.subtype:
 * - subtype "top only" or "top" = top only
 * - metadata.visibleFaces.frontAndTop = true = front and top
 * - otherwise = front only (default)
 */
export const slideIn: TransitionHelper = (ctx: TransitionContext): TransitionResult => {
	const { object, metadata, animationManager, phaseTimings, debugManager } = ctx;

	// Phase timings with defaults
	const fadeInDuration = phaseTimings?.fadeIn ?? 300;
	const slideDuration = phaseTimings?.slide ?? 400;

	// Offset distance: 18 inches in scene units
	const offsetDistance = 18;

	// Store target position (where the object should end up)
	const targetPosition = object.position.clone();

	// Determine visible faces from metadata.subtype (preferred) or userData.subtype (fallback)
	// Placement values: "front only" (default), "front and top", "top only" or "top"
	const metadataSubtype = (metadata as any).subtype;
	const userDataSubtype = object.userData?.subtype;
	const rawSubtype = metadataSubtype ?? userDataSubtype ?? 'front only';
	const subtype = (typeof rawSubtype === 'string' ? rawSubtype : 'front only').toLowerCase().trim();

	// Detect placement type from subtype string
	const isTopOnly = subtype === 'top only' || subtype === 'top';
	const isFrontAndTop = subtype === 'front and top' || (subtype.includes('front') && subtype.includes('top'));

	// Get rotations for transformation
	// The object's "front face" direction depends on the object's local Y rotation
	// Note: object.position is in PARENT space, so the offset must also be in parent space
	// This means we only use the object's LOCAL rotation for the offset direction,
	// not the parent's rotation (which is already part of the coordinate system)
	const { parent } = object;
	const objectRotationY = object.rotation.y;
	const parentRotationY = parent?.rotation.y ?? 0;
	// World rotation is only used for debug visualization (world-space face centers)
	const worldRotationY = objectRotationY + parentRotationY;

	// Calculate offset direction based on visible faces
	// In local space: front is +Z, top is +Y
	let offsetDirection: Vector3;

	if (isFrontAndTop) {
		// 45 degrees between front (+Z) and top (+Y)
		// normalize(0, 1, 1) gives equal parts Y and Z
		offsetDirection = new Vector3(0, 1, 1).normalize();
	} else if (isTopOnly) {
		// Slide from above (pure +Y)
		offsetDirection = new Vector3(0, 1, 0);
	} else {
		// Front only: slide from front (pure +Z, toward camera)
		offsetDirection = new Vector3(0, 0, 1);
	}

	// Transform offset direction by OBJECT's local Y rotation only
	// Since object.position is in parent-local space, the offset must also be in parent-local space
	// The parent's rotation doesn't affect where +Z is relative to the object in parent coordinates
	const cos = Math.cos(objectRotationY);
	const sin = Math.sin(objectRotationY);
	const rotatedX = offsetDirection.x * cos - offsetDirection.z * sin;
	const rotatedZ = offsetDirection.x * sin + offsetDirection.z * cos;
	offsetDirection.x = rotatedX;
	offsetDirection.z = rotatedZ;

	// Calculate start position (offset from target)
	const startPosition = targetPosition.clone().add(
		offsetDirection.clone().multiplyScalar(offsetDistance)
	);

	// Debug visualization: Calculate face centers from bounding box
	const debugId = `slide-${metadata.id}`;
	// Face offsets from object origin (in world space)
	let faceOffsets: { frontOffset: Vector3; topOffset: Vector3 } | null = null;

	// Ensure matrices are up to date before computing bounding box
	object.updateMatrixWorld(true);

	// Use world-space bounding box (reliable, works for all objects)
	const worldBoundingBox = new Box3().setFromObject(object);

	const isBoundingBoxValid = Number.isFinite(worldBoundingBox.max.x) &&
		Number.isFinite(worldBoundingBox.max.y) &&
		Number.isFinite(worldBoundingBox.max.z);

	if (isBoundingBoxValid) {
		const worldCenter = new Vector3();
		const worldSize = new Vector3();
		worldBoundingBox.getCenter(worldCenter);
		worldBoundingBox.getSize(worldSize);

		// Get object's current world position
		const objectWorldPos = new Vector3();
		object.getWorldPosition(objectWorldPos);

		// Get depth from metadata if available (more accurate than geometry bounds)
		const metadataDepth = (metadata as any).depth ?? object.userData?.depth;
		let localDepth = metadataDepth ?? worldSize.z; // fallback to world size

		// If no metadata depth, try to get from geometry bounds
		if (!metadataDepth) {
			object.traverse((child) => {
				if ((child as Mesh).isMesh && (child as Mesh).geometry) {
					const mesh = child as Mesh;
					if (!mesh.geometry.boundingBox) {
						mesh.geometry.computeBoundingBox();
					}
					if (mesh.geometry.boundingBox) {
						const geomSize = new Vector3();
						mesh.geometry.boundingBox.getSize(geomSize);
						// Use the geometry's Z size as local depth (first mesh found)
						localDepth = geomSize.z;
					}
				}
			});
		}

		// Front face: project from world center along the rotated front direction
		// The front normal in world space after Y rotation
		const cosR = Math.cos(worldRotationY);
		const sinR = Math.sin(worldRotationY);
		const frontNormal = new Vector3(sinR, 0, cosR);

		// Calculate front face center
		const halfLocalDepth = localDepth / 2;
		let frontFaceCenterWorld: Vector3;

		const isAppliance = (metadata as any).type === 'appliance' || object.userData?.type === 'appliance';
		if (isAppliance) {
			// Appliances: origin is at the front face, so project worldCenter onto the front face plane
			// This keeps X,Y centered but uses the correct depth from the object origin
			const centerToOrigin = objectWorldPos.clone().sub(worldCenter);
			const distanceAlongNormal = centerToOrigin.dot(frontNormal);
			frontFaceCenterWorld = worldCenter.clone().add(frontNormal.clone().multiplyScalar(distanceAlongNormal));
		} else {
			// Non-appliances: front face is at world center + front normal * half depth
			frontFaceCenterWorld = worldCenter.clone().add(
				frontNormal.clone().multiplyScalar(halfLocalDepth)
			);
		}

		// Top face center: simply at max Y, centered on X and Z of world center
		const topFaceCenterWorld = new Vector3(worldCenter.x, worldBoundingBox.max.y, worldCenter.z);

		// Store as offsets from object world position
		faceOffsets = {
			frontOffset: frontFaceCenterWorld.clone().sub(objectWorldPos),
			topOffset: topFaceCenterWorld.clone().sub(objectWorldPos)
		};
	}

	// Helper to compute face centers in world space
	// objectLocalPos is the object's position in PARENT space (local position)
	const computeFaceCenters = (objectLocalPos: Vector3): { frontCenter: Vector3; topCenter: Vector3; frontEnd: Vector3; topEnd: Vector3 } | null => {
		if (!faceOffsets) return null;

		// Convert object's local position to world position
		const objectWorldPos = parent ? localToWorld(objectLocalPos, parent) : objectLocalPos.clone();

		// Face centers in world space = object world position + stored offsets
		const frontCenter = objectWorldPos.clone().add(faceOffsets.frontOffset);
		const topCenter = objectWorldPos.clone().add(faceOffsets.topOffset);

		// Front normal rotated by world rotation (pointing outward from front face)
		const cosR = Math.cos(worldRotationY);
		const sinR = Math.sin(worldRotationY);
		const frontNormal = new Vector3(sinR, 0, cosR);
		const frontEnd = frontCenter.clone().add(frontNormal.clone().multiplyScalar(18));

		// Top normal (always up, pointing away from top face)
		const topNormal = new Vector3(0, 1, 0);
		const topEnd = topCenter.clone().add(topNormal.clone().multiplyScalar(18));

		return { frontCenter, topCenter, frontEnd, topEnd };
	};

	// Create debug visualization if debug mode is enabled
	if (debugManager?.visible && faceOffsets) {
		const initial = computeFaceCenters(startPosition);
		if (initial) {
			// Front face (cyan)
			debugManager.addSphere({
				id: `${debugId}-front-face`,
				position: initial.frontCenter,
				radius: 0.5,
				color: 0x00ffff,
				label: 'Front Face'
			});
			debugManager.addLine({
				id: `${debugId}-front-vector`,
				start: initial.frontCenter,
				end: initial.frontEnd,
				color: 0x00ffff
			});

			// Top face (green)
			debugManager.addSphere({
				id: `${debugId}-top-face`,
				position: initial.topCenter,
				radius: 0.5,
				color: 0x00ff00,
				label: 'Top Face'
			});
			debugManager.addLine({
				id: `${debugId}-top-vector`,
				start: initial.topCenter,
				end: initial.topEnd,
				color: 0x00ff00
			});
		}
	}

	// Helper to update face vectors during animation
	const updateFaceVectors = (currentPos: Vector3): void => {
		if (!debugManager?.visible || !faceOffsets) return;

		const centers = computeFaceCenters(currentPos);
		if (centers) {
			debugManager.updateSphere(`${debugId}-front-face`, centers.frontCenter);
			debugManager.updateLine(`${debugId}-front-vector`, centers.frontCenter, centers.frontEnd);
			debugManager.updateSphere(`${debugId}-top-face`, centers.topCenter);
			debugManager.updateLine(`${debugId}-top-vector`, centers.topCenter, centers.topEnd);
		}
	};

	// Cleanup debug visualization
	const cleanupDebug = (): void => {
		if (debugManager) {
			debugManager.removeSphere(`${debugId}-front-face`);
			debugManager.removeLine(`${debugId}-front-vector`);
			debugManager.removeSphere(`${debugId}-top-face`);
			debugManager.removeLine(`${debugId}-top-vector`);
		}
	};

	// Set initial state: at offset position, invisible
	object.position.copy(startPosition);
	prepareMaterialsForFade(object);
	setObjectOpacity(object, 0);

	// Track animation state for cancellation
	let cancelled = false;

	const executeAnimation = async (): Promise<void> => {
		// PHASE 1: Fade In (stationary at start position)
		await animationManager.animateOpacity(
			`slide-fade-${metadata.id}`,
			0,
			1,
			fadeInDuration,
			(opacity) => setObjectOpacity(object, opacity),
			{ easing: easeOutCubic }
		);

		if (cancelled) return;

		// PHASE 2: Slide to target position with ease-in-out (slow -> fast -> slow)
		await new Promise<void>((resolve) => {
			animationManager.animatePosition(
				`slide-pos-${metadata.id}`,
				startPosition,
				targetPosition,
				slideDuration,
				(pos) => {
					object.position.copy(pos);
					updateFaceVectors(pos);
				},
				{ easing: easeInOutCubic, onComplete: resolve }
			);
		});

		// Ensure exact final state
		restoreMaterials(object);
		setObjectOpacity(object, 1); // Ensure fully visible after fade-in
		object.position.copy(targetPosition);
		// Update face vectors to final position (keep them visible for debugging)
		updateFaceVectors(targetPosition);
	};

	const promise = executeAnimation().catch(() => {
		// Ensure final state even on error
		restoreMaterials(object);
		setObjectOpacity(object, 1); // Ensure fully visible
		object.position.copy(targetPosition);
		cleanupDebug();
	});

	return {
		promise,
		cancel: () => {
			cancelled = true;
			animationManager.stop(`slide-fade-${metadata.id}`);
			animationManager.stop(`slide-pos-${metadata.id}`);
			restoreMaterials(object);
			setObjectOpacity(object, 1); // Ensure fully visible
			object.position.copy(targetPosition);
			cleanupDebug();
		}
	};
};
